/**
 * TravelerForm Component
 * VOY-228: Consolidated traveler form for both flight passengers and hotel guests
 */

import { useState } from 'react'
import { ChevronDown, ChevronUp, User, Copy, Plane, Hotel } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { 
  COUNTRY_PHONE_CONFIGS, 
  formatPhoneNumber, 
  getPhoneConfigByDial,
  parsePhoneDigits
} from '@/lib/phone'
import type { PassengerDetail, GuestDetail, IdentityDocument } from '@/types/cart'

// Sorted country codes for phone selection
const PHONE_COUNTRY_CODES = [
  ...COUNTRY_PHONE_CONFIGS.filter(c => ['US', 'CA', 'GB', 'MX', 'AR', 'BR', 'DE', 'FR', 'ES', 'IT', 'AU', 'JP', 'CN', 'IN', 'KR'].includes(c.code)),
  ...COUNTRY_PHONE_CONFIGS.filter(c => !['US', 'CA', 'GB', 'MX', 'AR', 'BR', 'DE', 'FR', 'ES', 'IT', 'AU', 'JP', 'CN', 'IN', 'KR'].includes(c.code))
]

// Country list for passport issuing
const PASSPORT_COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AR', name: 'Argentina' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'JP', name: 'Japan' },
  { code: 'CN', name: 'China' },
  { code: 'IN', name: 'India' },
  { code: 'AU', name: 'Australia' },
  { code: 'KR', name: 'South Korea' },
]

/** Default age for child travelers when not specified (1–17; RateHawk requires age). */
const DEFAULT_CHILD_AGE = 8

interface TravelerFormProps {
  // Cart context
  hasFlights: boolean
  hasHotels: boolean
  
  // Passenger data (for flights)
  passengers: PassengerDetail[]
  onUpdatePassenger: (index: number, updates: Partial<PassengerDetail>) => void
  onUpdatePassengerDocument: (passengerIndex: number, docIndex: number, updates: Partial<IdentityDocument>) => void
  
  // Guest data (for hotels)
  guests: GuestDetail[]
  onUpdateGuest: (index: number, updates: Partial<GuestDetail>) => void
  
  /** When hasHotels: optional summary from cart e.g. { adults: 2, children: 1 } to show "2 adults, 1 child" */
  hotelGuestSummary?: { adults: number; children: number }
  /** VOY-298: Child ages (1–17) for hotel; required by RateHawk. Editable in checkout. */
  childAges?: number[]
  onChildAgesChange?: (ages: number[]) => void
  /** Child names (editable); same length as child count. */
  childNames?: { first_name: string; last_name: string }[]
  onChildNamesChange?: (names: { first_name: string; last_name: string }[]) => void
  
  // Primary contact sync
  primaryFirstName: string
  primaryLastName: string
  primaryEmail: string
}

export function TravelerForm({
  hasFlights,
  hasHotels,
  passengers,
  onUpdatePassenger,
  onUpdatePassengerDocument,
  guests,
  onUpdateGuest,
  hotelGuestSummary,
  childAges = [],
  onChildAgesChange,
  childNames = [],
  onChildNamesChange,
  primaryFirstName,
  primaryLastName,
  primaryEmail,
}: TravelerFormProps) {
  // Track which travelers are expanded
  const [expandedTravelers, setExpandedTravelers] = useState<Set<number>>(new Set([0]))
  
  const toggleTraveler = (index: number) => {
    const newExpanded = new Set(expandedTravelers)
    if (newExpanded.has(index)) {
      newExpanded.delete(index)
    } else {
      newExpanded.add(index)
    }
    setExpandedTravelers(newExpanded)
  }
  
  // Total travelers: adults (passengers/guests) + children when hotel has children
  const adultCount = Math.max(passengers.length, guests.length)
  const childCount = hasHotels && hotelGuestSummary?.children ? hotelGuestSummary.children : 0
  const travelerCount = adultCount + childCount
  
  // Copy primary contact info to a traveler
  const copyFromPrimary = (index: number) => {
    if (hasFlights && passengers[index]) {
      onUpdatePassenger(index, {
        given_name: primaryFirstName,
        family_name: primaryLastName,
        email: primaryEmail,
      })
    }
    if (hasHotels && guests[index]) {
      onUpdateGuest(index, {
        first_name: primaryFirstName,
        last_name: primaryLastName,
        email: primaryEmail,
      })
    }
  }
  
  if (travelerCount === 0) {
    return null
  }
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Traveler Information
        </CardTitle>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {hasFlights && (
            <Badge variant="secondary" className="gap-1">
              <Plane className="h-3 w-3" />
              {passengers.length} passenger{passengers.length !== 1 ? 's' : ''}
            </Badge>
          )}
          {hasHotels && (
            <Badge variant="secondary" className="gap-1">
              <Hotel className="h-3 w-3" />
              {hotelGuestSummary && hotelGuestSummary.children > 0
                ? `${hotelGuestSummary.adults} adult${hotelGuestSummary.adults !== 1 ? 's' : ''}, ${hotelGuestSummary.children} child${hotelGuestSummary.children !== 1 ? 'ren' : ''}`
                : `${guests.length} guest${guests.length !== 1 ? 's' : ''}`}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {Array.from({ length: travelerCount }).map((_, index) => {
          const isChildSlot = hasHotels && childCount > 0 && index >= adultCount
          const childIndex = isChildSlot ? index - adultCount : 0

          if (isChildSlot) {
            const ages = childAges.length >= childCount ? childAges : [...childAges, ...Array(childCount - childAges.length).fill(DEFAULT_CHILD_AGE)]
            const age = ages[childIndex] ?? DEFAULT_CHILD_AGE
            const names = childNames.length >= childCount ? childNames : [...childNames, ...Array.from({ length: childCount - childNames.length }, () => ({ first_name: '', last_name: '' }))]
            const nameEntry = names[childIndex] ?? { first_name: '', last_name: '' }
            const displayName = [nameEntry.first_name, nameEntry.last_name].filter(Boolean).join(' ').trim() || `Child ${childIndex + 1}`
            const isExpanded = expandedTravelers.has(index)
            return (
              <Collapsible
                key={index}
                open={isExpanded}
                onOpenChange={() => toggleTraveler(index)}
              >
                <div className="border rounded-lg overflow-hidden">
                  <div className="flex items-center">
                    <CollapsibleTrigger asChild>
                      <button
                        type="button"
                        className="flex-1 flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary">{index + 1}</span>
                          </div>
                          <div className="text-left">
                            <div className="font-medium">{displayName}</div>
                            <div className="text-sm text-muted-foreground">
                              Age (1–17) required for hotel • Guest
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">{age} years</span>
                          {isExpanded ? (
                            <ChevronUp className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                      </button>
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent>
                    <div className="p-4 pt-0 space-y-4 border-t">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <Hotel className="h-4 w-4" />
                        Child information
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>First Name</Label>
                          <Input
                            value={nameEntry.first_name}
                            onChange={(e) => {
                              const next = names.slice(0, childCount).map((n, i) => (i === childIndex ? { ...n, first_name: e.target.value } : n))
                              onChildNamesChange?.(next)
                            }}
                            placeholder="Child first name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Last Name</Label>
                          <Input
                            value={nameEntry.last_name}
                            onChange={(e) => {
                              const next = names.slice(0, childCount).map((n, i) => (i === childIndex ? { ...n, last_name: e.target.value } : n))
                              onChildNamesChange?.(next)
                            }}
                            placeholder="Child last name"
                          />
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        The hotel requires each child&apos;s age (1–17) for correct rates and policies.
                      </p>
                      <div className="space-y-2">
                        <Label>Age *</Label>
                        <select
                          value={age}
                          onChange={(e) => {
                            const newAge = Math.min(17, Math.max(1, parseInt(e.target.value, 10) || DEFAULT_CHILD_AGE))
                            const next = ages.slice(0, childCount)
                            next[childIndex] = newAge
                            onChildAgesChange?.(next)
                          }}
                          className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                        >
                          {Array.from({ length: 17 }, (_, j) => j + 1).map((a) => (
                            <option key={a} value={a}>{a} years</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            )
          }

          const passenger = passengers[index]
          const guest = guests[index]
          const isExpanded = expandedTravelers.has(index)
          const isPrimary = index === 0

          return (
            <Collapsible 
              key={index} 
              open={isExpanded} 
              onOpenChange={() => toggleTraveler(index)}
            >
              <div className="border rounded-lg overflow-hidden">
                <div className="flex items-center">
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="flex-1 flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary">{index + 1}</span>
                        </div>
                        <div className="text-left">
                          <div className="font-medium">
                            {passenger?.given_name || guest?.first_name || `Traveler ${index + 1}`}
                            {' '}
                            {passenger?.family_name || guest?.last_name || ''}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {isPrimary ? 'Primary Contact' : `Additional Traveler`}
                            {hasFlights && hasHotels && ' • Passenger & Guest'}
                            {hasFlights && !hasHotels && ' • Passenger'}
                            {!hasFlights && hasHotels && ' • Guest'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                    </button>
                  </CollapsibleTrigger>
                </div>
                
                <CollapsibleContent>
                  <div className="p-4 pt-0 space-y-4 border-t">
                    {/* Name fields - always shown */}
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Title *</Label>
                        <Select 
                          value={passenger?.title || guest?.title || 'mr'} 
                          onValueChange={(value: string) => {
                            if (hasFlights && passenger) onUpdatePassenger(index, { title: value as PassengerDetail['title'] })
                            if (hasHotels && guest) onUpdateGuest(index, { title: value as GuestDetail['title'] })
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mr">Mr</SelectItem>
                            <SelectItem value="mrs">Mrs</SelectItem>
                            <SelectItem value="ms">Ms</SelectItem>
                            <SelectItem value="miss">Miss</SelectItem>
                            <SelectItem value="dr">Dr</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {!isPrimary && (
                        <>
                          <div className="space-y-2">
                            <Label>First Name *</Label>
                            <Input
                              value={passenger?.given_name || guest?.first_name || ''}
                              onChange={(e) => {
                                if (hasFlights && passenger) onUpdatePassenger(index, { given_name: e.target.value })
                                if (hasHotels && guest) onUpdateGuest(index, { first_name: e.target.value })
                              }}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Last Name *</Label>
                            <Input
                              value={passenger?.family_name || guest?.last_name || ''}
                              onChange={(e) => {
                                if (hasFlights && passenger) onUpdatePassenger(index, { family_name: e.target.value })
                                if (hasHotels && guest) onUpdateGuest(index, { last_name: e.target.value })
                              }}
                              required
                            />
                          </div>
                        </>
                      )}
                      
                      {isPrimary && (
                        <>
                          <div className="space-y-2">
                            <Label>First Name</Label>
                            <Input
                              value={primaryFirstName}
                              disabled
                              className="bg-muted"
                            />
                            <p className="text-xs text-muted-foreground">From customer info</p>
                          </div>
                          <div className="space-y-2">
                            <Label>Last Name</Label>
                            <Input
                              value={primaryLastName}
                              disabled
                              className="bg-muted"
                            />
                            <p className="text-xs text-muted-foreground">From customer info</p>
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* Flight-specific fields */}
                    {hasFlights && passenger && (
                      <>
                        <Separator />
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                            <Plane className="h-4 w-4" />
                            Flight Information
                          </div>
                          
                          <div className="grid sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Gender *</Label>
                              <Select 
                                value={passenger.gender} 
                                onValueChange={(value: string) => onUpdatePassenger(index, { gender: value as PassengerDetail['gender'] })}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="m">Male</SelectItem>
                                  <SelectItem value="f">Female</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div className="space-y-2">
                              <Label>Date of Birth *</Label>
                              <Input
                                type="date"
                                value={passenger.born_on}
                                onChange={(e) => onUpdatePassenger(index, { born_on: e.target.value })}
                                max={new Date().toISOString().split('T')[0]}
                                required
                              />
                            </div>
                          </div>
                          
                          {!isPrimary && (
                            <div className="grid sm:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label>Email *</Label>
                                <Input
                                  type="email"
                                  value={passenger.email}
                                  onChange={(e) => onUpdatePassenger(index, { email: e.target.value })}
                                  required
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Phone *</Label>
                                <div className="flex gap-2">
                                  <Select 
                                    key={`phone-select-traveler-${index}`}
                                    value={`traveler-${index}-${getPhoneConfigByDial(passenger.phone_country_code || '+1')?.code ?? 'US'}`}
                                    onValueChange={(value) => {
                                      const isoCode = value.replace(`traveler-${index}-`, '')
                                      const config = PHONE_COUNTRY_CODES.find((c) => c.code === isoCode)
                                      if (config) onUpdatePassenger(index, { phone_country_code: config.dial })
                                    }}
                                  >
                                    <SelectTrigger className="w-[100px]">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[300px]">
                                      {PHONE_COUNTRY_CODES.map((c) => (
                                        <SelectItem 
                                          key={`traveler-${index}-phone-${c.code}`}
                                          value={`traveler-${index}-${c.code}`}
                                        >
                                          {c.flag} {c.dial}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <Input
                                    type="tel"
                                    placeholder={getPhoneConfigByDial(passenger.phone_country_code || '+1')?.placeholder || '(555) 123-4567'}
                                    value={passenger.phone_number}
                                    onChange={(e) => {
                                      const config = getPhoneConfigByDial(passenger.phone_country_code || '+1')
                                      const digits = parsePhoneDigits(e.target.value)
                                      const formatted = config ? formatPhoneNumber(digits, config.code) : digits
                                      onUpdatePassenger(index, { phone_number: formatted })
                                    }}
                                    className="flex-1"
                                    required
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Passport Section */}
                          <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                            <Label className="font-medium">Passport Information</Label>
                            
                            <div className="grid sm:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label>Passport Number *</Label>
                                <Input
                                  value={passenger.identity_documents?.[0]?.unique_identifier || ''}
                                  onChange={(e) => onUpdatePassengerDocument(index, 0, { 
                                    unique_identifier: e.target.value.toUpperCase() 
                                  })}
                                  placeholder="A12345678"
                                  required
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Issuing Country *</Label>
                                <Select 
                                  value={passenger.identity_documents?.[0]?.issuing_country_code || 'US'} 
                                  onValueChange={(value) => onUpdatePassengerDocument(index, 0, { 
                                    issuing_country_code: value 
                                  })}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="max-h-[300px]">
                                    {PASSPORT_COUNTRIES.map(country => (
                                      <SelectItem key={country.code} value={country.code}>
                                        {country.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            
                            <div className="space-y-2">
                              <Label>Expiry Date *</Label>
                              <Input
                                type="date"
                                value={passenger.identity_documents?.[0]?.expires_on || ''}
                                onChange={(e) => onUpdatePassengerDocument(index, 0, { 
                                  expires_on: e.target.value 
                                })}
                                min={new Date().toISOString().split('T')[0]}
                                required
                              />
                              <p className="text-xs text-muted-foreground">
                                Must be valid for at least 6 months from travel date
                              </p>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                    
                    {/* Hotel-specific fields - only for non-primary guests when no flight */}
                    {hasHotels && guest && !hasFlights && !isPrimary && (
                      <>
                        <Separator />
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                            <Hotel className="h-4 w-4" />
                            Guest Information
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Additional guests only need name information. The lead guest's email will be used for booking confirmation.
                          </p>
                        </div>
                      </>
                    )}
                    
                    {/* Copy button for non-primary travelers */}
                    {!isPrimary && (
                      <div className="pt-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => copyFromPrimary(index)}
                          className="text-xs"
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          Copy info from primary contact
                        </Button>
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          )
        })}
      </CardContent>
    </Card>
  )
}


